/**
 * Vista de Eventos para Director de Club
 * - Eventos publicados de la asociación/unión donde puede inscribir miembros
 * - Eventos propios del club (para gestionar)
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, Button, Badge } from "@/components/ui";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import {
  Calendar,
  Plus,
  Search,
  MapPin,
  Users,
  Edit,
  Trash2,
  Loader2,
  ClipboardList,
  Ticket,
  Upload,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import {
  useEvents,
  useDeleteEvent,
  useEventRegistrations,
  useClubRegistrations,
} from "@/hooks/useEvents";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import {
  getEventStatusLabel,
  getEventStatusColor,
} from "@/lib/services/events";
import EventModal from "./EventModal";
import EventRegistrationModal from "./EventRegistrationModal";
import PaymentUploadModal from "./PaymentUploadModal";
import ClubEventCard from "./ClubEventCard";
import type { Event } from "@/types";
import { getActiveSections } from "@/lib/utils";
import { Can } from "@/components/auth/Can";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

type TabType = "available" | "my-events" | "my-registrations";

export default function ClubEventsView() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("available");
  const [searchTerm, setSearchTerm] = useState("");
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationEvent, setRegistrationEvent] = useState<Event | null>(
    null,
  );
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptEvent, setReceiptEvent] = useState<Event | null>(null);

  const { data: events, isLoading } = useEvents();
  const { data: clubs } = useAccessibleClubs();
  const deleteMutation = useDeleteEvent();
  const confirm = useConfirm();

  // Obtener inscripciones del evento seleccionado para evitar duplicados
  const { data: existingRegistrations } = useEventRegistrations(
    registrationEvent?.id,
  );

  const myClub = clubs?.[0];

  // Obtener todas las inscripciones del club para determinar pagos pendientes
  const { data: clubRegistrations } = useClubRegistrations(myClub?.id);

  // Set de eventos que tienen inscripciones aprobadas pendientes de pago
  // Set de eventos con al menos una inscripción activa del club
  const eventsWithRegistrations = useMemo(() => {
    if (!clubRegistrations) return new Set<string>();
    return new Set(
      clubRegistrations
        .filter((r) => ["pending", "approved", "waitlist"].includes(r.status))
        .map((r) => r.eventId),
    );
  }, [clubRegistrations]);

  // Estadísticas del club por evento (inscritos)
  const clubEventStats = useMemo(() => {
    if (!clubRegistrations) return new Map<string, { total: number }>();
    const map = new Map<string, { total: number }>();
    clubRegistrations
      .filter((r) => ["pending", "approved", "waitlist"].includes(r.status))
      .forEach((r) => {
        const cur = map.get(r.eventId) || { total: 0 };
        cur.total++;
        map.set(r.eventId, cur);
      });
    return map;
  }, [clubRegistrations]);

  // IDs de personas ya inscritas activamente en el evento (excluir rechazados/cancelados)
  const registeredPersonIds = useMemo(() => {
    if (!existingRegistrations) return [];
    return existingRegistrations
      .filter((r) => ["pending", "approved", "waitlist"].includes(r.status))
      .map((r) => r.personId || r.memberId)
      .filter((id): id is string => Boolean(id));
  }, [existingRegistrations]);

  // Eventos disponibles para inscripción (de niveles superiores, publicados)
  const availableEvents = useMemo(() => {
    if (!events || !myClub) return [];

    return events.filter((e) => {
      // Solo eventos publicados de niveles superiores
      const isHigherLevel = ["association", "union", "district"].includes(
        e.level,
      );
      const isPublished = [
        "published",
        "registration_closed",
        "in_progress",
        "completed",
      ].includes(e.status);

      // Verificar si el tipo de club está permitido
      const clubTypeAllowed = e.clubTypesAllowed?.some((ct: string) =>
        getActiveSections(myClub).includes(ct),
      );

      // Mostrar si: es próximo/activo, o si el club ya está inscrito (para ver historial)
      const isUpcoming = toSafeDate(e.startDate) >= new Date();
      const clubIsRegistered = eventsWithRegistrations.has(e.id);

      return (
        isHigherLevel &&
        isPublished &&
        clubTypeAllowed &&
        (isUpcoming || clubIsRegistered)
      );
    });
  }, [events, myClub, eventsWithRegistrations]);

  // Eventos organizados por mi club
  const myClubEvents = useMemo(() => {
    if (!events || !myClub) return [];
    return events.filter((e) => e.organizerId === myClub.id);
  }, [events, myClub]);

  // Separar disponibles próximos vs pasados (para mostrar sección "Pasados")
  const upcomingAvailableEvents = useMemo(
    () => availableEvents.filter((e) => toSafeDate(e.startDate) >= new Date()),
    [availableEvents],
  );
  const pastAvailableEvents = useMemo(
    () =>
      availableEvents
        .filter((e) => toSafeDate(e.startDate) < new Date())
        .sort(
          (a, b) =>
            toSafeDate(b.startDate).getTime() -
            toSafeDate(a.startDate).getTime(),
        ),
    [availableEvents],
  );

  // Filtrar según tab activo y búsqueda
  const filteredEvents = useMemo(() => {
    let result = activeTab === "available" ? availableEvents : myClubEvents;

    if (searchTerm) {
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.location.city.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return result;
  }, [activeTab, availableEvents, myClubEvents, searchTerm]);

  const handleRegisterMembers = (event: Event) => {
    setRegistrationEvent(event);
    setShowRegistrationModal(true);
  };

  const handleUploadReceipt = (event: Event) => {
    setReceiptEvent(event);
    setShowReceiptModal(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleDeleteEvent = async (event: Event) => {
    const ok = await confirm({
      title: "Eliminar evento",
      description: `¿Seguro que deseas eliminar el evento "${event.name}"? Se perderán las inscripciones asociadas.`,
      variant: "danger",
      confirmText: "Eliminar",
    });
    if (ok) {
      await deleteMutation.mutateAsync(event.id);
    }
  };

  const formatDate = (timestamp: unknown) => {
    return toSafeDate(timestamp).toLocaleDateString("es-CO", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateRange = (start: unknown, end: unknown) => {
    const startDate = toSafeDate(start);
    const endDate = toSafeDate(end);

    if (startDate.toDateString() === endDate.toDateString()) {
      return formatDate(start);
    }

    return `${startDate.toLocaleDateString("es-CO", { day: "numeric", month: "short" })} - ${endDate.toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 flex-shrink-0">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            Eventos
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:ml-[52px]">
            Inscribe a tu club en eventos y gestiona tus propias actividades
          </p>
        </div>
        <Can permission="events.write" clubId={myClub?.id}>
          <Button
            onClick={() => setShowEventModal(true)}
            disabled={!myClub}
            className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300 w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="sm:hidden">Nuevo evento</span>
            <span className="hidden sm:inline">Nuevo Evento del Club</span>
          </Button>
        </Can>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-blue-50/50">
          <CardContent className="py-4 px-4 sm:py-5 sm:px-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 leading-none">
                  {upcomingAvailableEvents.length}
                </p>
                <p className="text-[11px] sm:text-sm font-medium text-gray-500 mt-1 leading-tight">
                  Eventos Disponibles
                </p>
                {pastAvailableEvents.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    +{pastAvailableEvents.length} pasado(s)
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-purple-50/50">
          <CardContent className="py-4 px-4 sm:py-5 sm:px-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 leading-none">
                  {clubRegistrations?.filter((r) =>
                    ["pending", "approved", "waitlist"].includes(r.status),
                  ).length ?? 0}
                </p>
                <p className="text-[11px] sm:text-sm font-medium text-gray-500 mt-1 leading-tight">
                  Miembros Inscritos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-amber-50/50">
          <CardContent className="py-4 px-4 sm:py-5 sm:px-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 leading-none">
                  {eventsWithRegistrations.size}
                </p>
                <p className="text-[11px] sm:text-sm font-medium text-gray-500 mt-1 leading-tight">
                  Eventos con Participación
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-green-50/50">
          <CardContent className="py-4 px-4 sm:py-5 sm:px-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 leading-none">
                  {
                    myClubEvents.filter(
                      (e) =>
                        e.status !== "completed" && e.status !== "cancelled",
                    ).length
                  }
                </p>
                <p className="text-[11px] sm:text-sm font-medium text-gray-500 mt-1 leading-tight">
                  Mis Eventos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-full sm:w-fit">
        <button
          onClick={() => setActiveTab("available")}
          className={`flex-1 sm:flex-initial px-3 sm:px-5 py-2.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
            activeTab === "available"
              ? "bg-white text-primary-600 shadow-md"
              : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
          }`}
        >
          <Ticket className="w-4 h-4 inline mr-1.5 sm:mr-2" />
          Disponibles ({upcomingAvailableEvents.length}
          {pastAvailableEvents.length > 0
            ? `+${pastAvailableEvents.length}`
            : ""}
          )
        </button>
        <button
          onClick={() => setActiveTab("my-events")}
          className={`flex-1 sm:flex-initial px-3 sm:px-5 py-2.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
            activeTab === "my-events"
              ? "bg-white text-primary-600 shadow-md"
              : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-1.5 sm:mr-2" />
          Mis Eventos ({myClubEvents.length})
        </button>
      </div>

      {/* Guía de flujo: solo para tab de eventos disponibles */}
      {activeTab === "available" && availableEvents.length > 0 && (
        <Card className="border-0 shadow-md bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50">
          <CardContent className="py-4 px-4 sm:px-6">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-indigo-500 flex-shrink-0" />
              ¿Cómo inscribir a tu club en un evento?
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs sm:text-sm">
              <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm">
                <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  1
                </div>
                <span className="text-gray-700 font-medium">
                  Inscribir miembros
                </span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 hidden sm:block" />
              <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm">
                <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  2
                </div>
                <span className="text-gray-700 font-medium">
                  Subir pago del club
                </span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 hidden sm:block" />
              <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm">
                <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  3
                </div>
                <span className="text-gray-700 font-medium">
                  Mín. 10 inscritos + 1 pago = Ranking
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar eventos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
          className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
        />
      </div>

      {/* Events List */}
      {(() => {
        const hasEvents =
          activeTab === "available"
            ? availableEvents.length > 0
            : filteredEvents.length > 0;
        if (!hasEvents) {
          return (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Calendar className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {activeTab === "available"
                    ? "No hay eventos disponibles"
                    : "No tienes eventos"}
                </h3>
                <p className="text-gray-500 mt-1">
                  {activeTab === "available"
                    ? "No hay eventos próximos de la asociación para tu club"
                    : "Crea un nuevo evento para tu club"}
                </p>
                {activeTab === "my-events" && (
                  <Button
                    onClick={() => setShowEventModal(true)}
                    className="mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Evento
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        }
        return (
          <div className="grid gap-4">
            {activeTab === "available" && myClub ? (
              <>
                {/* Upcoming events */}
                {(() => {
                  const filtered = upcomingAvailableEvents.filter((e) =>
                    searchTerm
                      ? e.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        e.location.city
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      : true,
                  );
                  return filtered.map((event) => (
                    <ClubEventCard
                      key={event.id}
                      event={event}
                      clubId={myClub.id}
                      clubName={myClub.name}
                      clubRegistrations={
                        clubRegistrations?.filter(
                          (r) => r.eventId === event.id,
                        ) ?? []
                      }
                      onRegisterMembers={handleRegisterMembers}
                      onUploadPayment={handleUploadReceipt}
                    />
                  ));
                })()}

                {/* Past events where club was registered */}
                {(() => {
                  const filtered = pastAvailableEvents.filter((e) =>
                    searchTerm
                      ? e.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        e.location.city
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      : true,
                  );
                  if (filtered.length === 0) return null;
                  return (
                    <>
                      <div className="flex items-center gap-3 pt-2">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <ClipboardList className="w-3.5 h-3.5" />
                          Eventos pasados ({filtered.length})
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      {filtered.map((event) => (
                        <ClubEventCard
                          key={event.id}
                          event={event}
                          clubId={myClub.id}
                          clubName={myClub.name}
                          clubRegistrations={
                            clubRegistrations?.filter(
                              (r) => r.eventId === event.id,
                            ) ?? []
                          }
                          onRegisterMembers={handleRegisterMembers}
                          onUploadPayment={handleUploadReceipt}
                        />
                      ))}
                    </>
                  );
                })()}
              </>
            ) : (
              filteredEvents.map((event) => (
                <Card
                  key={event.id}
                  className="hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  <div className="flex">
                    {event.coverImageUrl ? (
                      <div className="w-48 h-auto flex-shrink-0">
                        <img
                          src={event.coverImageUrl}
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-48 h-auto flex-shrink-0 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-white/50" />
                      </div>
                    )}

                    <CardContent className="p-6 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {event.name}
                            </h3>
                            <Badge
                              className={getEventStatusColor(event.status)}
                            >
                              {getEventStatusLabel(event.status)}
                            </Badge>
                          </div>

                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {event.description}
                          </p>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDateRange(event.startDate, event.endDate)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {event.location.city}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {event.registeredCount || 0} inscritos
                              {event.registrationConfig?.maxCapacity &&
                                ` / ${event.registrationConfig.maxCapacity}`}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => navigate(`/events/${event.id}`)}
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors duration-200"
                            title="Ver evento"
                          >
                            <ClipboardList className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleEditEvent(event)}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors duration-200"
                            title="Editar"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          {event.status === "draft" && (
                            <button
                              onClick={() => handleDeleteEvent(event)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200"
                              title="Eliminar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))
            )}
          </div>
        );
      })()}

      {/* Modals */}
      {myClub && (
        <EventModal
          isOpen={showEventModal}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          organizerId={myClub.id}
          organizerName={myClub.name}
          event={selectedEvent}
        />
      )}

      {myClub && registrationEvent && (
        <EventRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => {
            setShowRegistrationModal(false);
            setRegistrationEvent(null);
          }}
          event={registrationEvent}
          clubId={myClub.id}
          clubName={myClub.name}
          clubChurchId={myClub.churchId}
          registeredPersonIds={registeredPersonIds}
          currentClubRegisteredCount={
            clubEventStats.get(registrationEvent.id)?.total || 0
          }
        />
      )}

      {receiptEvent && myClub && (
        <PaymentUploadModal
          isOpen={showReceiptModal}
          onClose={() => {
            setShowReceiptModal(false);
            setReceiptEvent(null);
          }}
          event={receiptEvent}
          clubId={myClub.id}
          clubName={myClub.name}
        />
      )}
    </div>
  );
}
